<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="[sizeClass, running ? 'vent-pin--active' : '']"
      :style="{ '--pin-active-color': ACCENT }"
      :title="device.deviceName">
      <!-- The drawn wind inside the status ring (P13); the ring carries the
           unit's speed, and the filter badge keeps its seat on the pin's rim. -->
      <ControlStatusRing :value="ringValue">
        <div class="device-icon-pin__icon" :style="{ color: running ? ACCENT : undefined }">
          <ControlIcon name="wind" :size="pinIconSize" :class="{ 'vent-breathe': running }" />
        </div>
      </ControlStatusRing>
      <span
        v-if="filterNeedsAttention"
        class="vent-pin__badge"
        :class="`vent-pin__badge--${filterTone}`"
        :title="filterTitle"></span>
    </div>
  </template>

  <!-- ── Button mode (compact card) ────────────────────────────────────── -->
  <!-- The card wears the other tiles' head — the unit's photo (or glyph) in the
       circle, then the name over mode · speed and the Outdoor → Supply ·
       Extract → Exhaust chain — and under it the HRV's own rows: the four mode
       chips and the bypass/filter state. Sizes are em against the button's
       container-query font scale; the train's labels drop out on narrow cards
       (below). -->
  <!-- ── Grid mode ─────────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || defaultIcon"
      :name="displayName"
      :is-on="running"
      active-color="amber-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode (expanded) ───────────────────────────────────────── -->
  <template v-else-if="mode === 'control'">
    <!-- The body of the sheet: the host owns the head and the panel, the body
         owns the train, the rows and the meta line (no head, no outer padding —
         the host's body container carries it). The ventilation family's amber
         rides down as the sheet accent; every primitive inside reads it. -->
    <div class="vent-sheet" :style="{ '--sheet-accent': ACCENT }">
      <!-- TRAIN — the HRV's signature: Outdoor → Supply · Extract → Exhaust,
           the four temperatures from the device's own readings, with the
           air-in / air-out direction icons. -->
      <div class="vent-train-card">
        <div class="vent-stop">
          <ControlIcon name="air-in" size="1.1875rem" />
          <span class="vent-stop-value">{{ tempText('temp_outdoor') }}</span>
          <span class="vent-stop-label">{{ tt('Outdoor', 'Zunanji') }}</span>
        </div>
        <span class="vent-arrow">→</span>
        <div class="vent-stop">
          <ControlIcon name="air-in" size="1.1875rem" />
          <span class="vent-stop-value">{{ tempText('temp_supply') }}</span>
          <span class="vent-stop-label">{{ tt('Supply', 'Dovod') }}</span>
        </div>
        <div class="vent-divider"></div>
        <div class="vent-stop">
          <ControlIcon name="air-out" size="1.1875rem" />
          <span class="vent-stop-value">{{ tempText('temp_extract') }}</span>
          <span class="vent-stop-label">{{ tt('Extract', 'Odvod') }}</span>
        </div>
        <span class="vent-arrow">→</span>
        <div class="vent-stop">
          <ControlIcon name="air-out" size="1.1875rem" />
          <span class="vent-stop-value">{{ tempText('temp_exhaust') }}</span>
          <span class="vent-stop-label">{{ tt('Exhaust', 'Izpuh') }}</span>
        </div>
      </div>

      <!-- MODE — the device's own enum options (Off / Manual / Auto / Boost),
           on the widget's existing `mode` write path. -->
      <div class="vent-chips">
        <ControlChip
          v-for="opt in modeOptions"
          :key="String(opt.value)"
          :active="isCurrentMode(opt)"
          :disabled="controlling"
          @click="handleSetMode(opt.value)">
          {{ modeWord(opt) }}
        </ControlChip>
      </div>

      <!-- SPEED — the manual speed slider on the existing `speed_percentage` path. -->
      <div class="vent-speed">
        <div class="vent-row-head">
          <span class="vent-row-key">{{ tt('Speed', 'Hitrost') }}</span>
          <span class="vent-row-value">{{ speedPercentText }}</span>
        </div>
        <ControlSlider :model-value="speedPercent ?? 0" :disabled="controlling" @change="handleSpeedValue" />
      </div>

      <!-- BYPASS — the summer bypass toggle row; FILTER + HUMIDITY — the
           readings, tinted by the severity the widget already folds
           (`filterNeedsAttention` / `filterTone`). -->
      <ControlToggleRow
        :model-value="bypassOn"
        icon="bypass"
        :label="tt('Summer bypass', 'Poletni obvod')"
        :disabled="controlling"
        @update:model-value="toggleBypass" />
      <div class="vent-tiles">
        <ControlValueTile
          icon="filter"
          :label="tt('Filter', 'Filter')"
          :value="filterWord"
          :tone="filterNeedsAttention ? 'accent' : 'muted'"
          :style="filterTileTint" />
        <ControlValueTile
          v-if="humidityText"
          icon="drop"
          :label="tt('Indoor humidity', 'Notranja vlažnost')"
          :value="humidityText" />
      </div>

      <p v-if="lastUpdated" class="vent-meta">
        <ControlIcon name="clock" size="0.8125rem" />
        {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
      </p>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { formatTime } from '../../utils/formatDate';
import { toFiniteNumber } from './deviceWidgetTypes';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import ControlSlider from '../device-sheet/ControlSlider.vue';
import ControlToggleRow from '../device-sheet/ControlToggleRow.vue';
import ControlValueTile from '../device-sheet/ControlValueTile.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import { useDeviceValueOptionsStore } from '../../stores/deviceValueOptions';
import { tt } from '../../utils/consoleText';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md' });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

/** One enum choice: the hub's value (`mode` 0…3, `filter_status` 0…2) and its own label. */
interface VentOption {
  value: number | string;
  label: string;
}

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();
const deviceValueOptionsStore = useDeviceValueOptionsStore();
const fetchedModeOptions = ref<VentOption[] | null>(null);
const fetchedFilterOptions = ref<VentOption[] | null>(null);

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

// ── Vocabulary ───────────────────────────────────────────────────────────
// The class's own enum values (Ventilation, hub card V1): mode 0 Off … 3 Boost,
// filter 0 OK … 2 Replace. The words the four known values read with are this
// build's own pairs, resolved through `tt()` at render — the hub's English
// labels would otherwise show through on a Slovene screen. A value this build
// does not know (a newer hub's fourth filter state) keeps the hub's own word.
const ACCENT = 'rgb(245, 158, 11)';

const MODE_WORDS: Record<string, [string, string]> = {
  '0': ['Off', 'Izklop'],
  '1': ['Manual', 'Ročno'],
  '2': ['Auto', 'Auto'],
  '3': ['Boost', 'Turbo'],
};
const FILTER_WORDS: Record<string, [string, string]> = {
  '0': ['OK', 'V redu'],
  '1': ['Due', 'Kmalu'],
  '2': ['Replace', 'Zamenjaj'],
};

const FALLBACK_MODE_OPTIONS: VentOption[] = [
  { value: 0, label: 'Off' },
  { value: 1, label: 'Manual' },
  { value: 2, label: 'Auto' },
  { value: 3, label: 'Boost' },
];
const FALLBACK_FILTER_OPTIONS: VentOption[] = [
  { value: 0, label: 'OK' },
  { value: 1, label: 'Due' },
  { value: 2, label: 'Replace' },
];

const modeOptions = computed<VentOption[]>(() =>
  fetchedModeOptions.value?.length ? fetchedModeOptions.value : FALLBACK_MODE_OPTIONS,
);
const filterOptions = computed<VentOption[]>(() =>
  fetchedFilterOptions.value?.length ? fetchedFilterOptions.value : FALLBACK_FILTER_OPTIONS,
);

function wordFor(words: Record<string, [string, string]>, option: VentOption): string {
  const pair = words[String(option.value)];
  return pair ? tt(pair[0], pair[1]) : String(option.label ?? option.value);
}

function modeWord(option: VentOption): string {
  return wordFor(MODE_WORDS, option);
}

/** The enum option a raw reading belongs to: by value first, then by its own label. */
function resolveOption(raw: unknown, options: VentOption[]): VentOption | null {
  if (raw === undefined || raw === null || raw === '') return null;
  const text = String(raw).trim();
  const byValue = options.find((option) => String(option.value) === text);
  if (byValue) return byValue;
  return options.find((option) => String(option.label).trim().toLowerCase() === text.toLowerCase()) ?? null;
}

// ── Readings ─────────────────────────────────────────────────────────────
/** A value's current reading: the live socket map first, the device row as a fallback. */
function readValue(key: string): unknown {
  if (key in currentValues.value) return currentValues.value[key];
  return props.device.deviceValues?.find((value) => value.valueKey === key)?.currentValue;
}

const modeRaw = computed(() => readValue('mode'));

function isCurrentMode(option: VentOption): boolean {
  const raw = modeRaw.value;
  if (raw === undefined || raw === null || raw === '') return false;
  const current = resolveOption(raw, modeOptions.value);
  const value = current ? current.value : raw;
  return String(value) === String(option.value);
}

/** Running for the card's active state: any mode but Off. An unreadable mode reads as off. */
const running = computed(() => {
  const raw = modeRaw.value;
  if (raw === undefined || raw === null || raw === '') return false;
  const option = resolveOption(raw, modeOptions.value);
  if (option) return String(option.value) !== '0';
  const text = String(raw).trim().toLowerCase();
  return !(text === '0' || text === 'off' || text === 'false' || text === 'izklop' || text === 'izklopljeno');
});

const speedPercent = computed(() => {
  const value = toFiniteNumber(readValue('speed_percentage'));
  return value === null ? null : Math.round(value);
});
const speedPercentText = computed(() => (speedPercent.value === null ? '--' : `${speedPercent.value}%`));

function toBoolean(raw: unknown): boolean | null {
  if (raw === undefined || raw === null || raw === '') return null;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw !== 0;
  const text = String(raw).trim().toLowerCase();
  if (['true', 'on', '1', 'yes'].includes(text)) return true;
  if (['false', 'off', '0', 'no'].includes(text)) return false;
  return null;
}

const bypassOn = computed(() => toBoolean(readValue('bypass')) === true);

const filterRaw = computed(() => readValue('filter_status'));
const filterOption = computed(() => resolveOption(filterRaw.value, filterOptions.value));
const filterWord = computed(() => {
  const option = filterOption.value;
  if (option) return wordFor(FILTER_WORDS, option);
  return filterRaw.value === undefined || filterRaw.value === null || filterRaw.value === ''
    ? '--'
    : String(filterRaw.value);
});
const filterTone = computed<'ok' | 'due' | 'replace' | 'unknown'>(() => {
  const option = filterOption.value;
  if (!option) return 'unknown';
  const value = String(option.value);
  if (value === '1') return 'due';
  if (value === '2') return 'replace';
  return 'ok';
});
const filterNeedsAttention = computed(() => filterTone.value === 'due' || filterTone.value === 'replace');
/**
 * The filter tile's own tint — the widget's existing severity fold, carried
 * into the sheet: `due` takes the amber every vent surface already uses,
 * `replace` the red the old chip carried, and an OK filter keeps the tile's
 * muted tone. The tint rides on the tile itself (inline `--sheet-accent`), so
 * it colours that one tile and nothing else in the sheet.
 */
const filterTileTint = computed<Record<string, string>>(() => {
  const tints: Record<string, string> = {};
  if (filterTone.value === 'replace') tints['--sheet-accent'] = 'rgb(239, 68, 68)';
  else if (filterTone.value === 'due') tints['--sheet-accent'] = 'rgb(245, 158, 11)';
  return tints;
});
const filterTitle = computed(() => {
  switch (filterTone.value) {
    case 'due':
      return tt('Filter replacement will be due soon', 'Kmalu bo treba zamenjati filter');
    case 'replace':
      return tt('Replace the filter', 'Zamenjajte filter');
    case 'ok':
      return tt('Filter is OK', 'Filter je v redu');
    default:
      return tt('Filter status', 'Stanje filtra');
  }
});

function tempOf(key: string): number | null {
  return toFiniteNumber(readValue(key));
}
function tempText(key: string): string {
  const value = tempOf(key);
  return value === null ? '--' : `${value.toFixed(1)}°C`;
}

const humidityText = computed(() => {
  const value = toFiniteNumber(readValue('humidity_indoor'));
  return value === null ? '' : `${Math.round(value)}%`;
});

// ── Actions ──────────────────────────────────────────────────────────────
async function handleSetMode(value: number | string) {
  if (controlling.value) return;
  await setProperty(props.device, 'mode', value);
}

async function toggleBypass() {
  if (controlling.value) return;
  await setProperty(props.device, 'bypass', !bypassOn.value);
}

/** The speed slider's released value — the same `speed_percentage` write path
 *  the old range input used (it wrote on change; `ControlSlider`'s `change`
 *  fires on release). */
async function handleSpeedValue(value: number) {
  if (!Number.isFinite(value)) return;
  await setProperty(props.device, 'speed_percentage', Math.max(0, Math.min(100, Math.round(value))));
}

// ── Display ──────────────────────────────────────────────────────────────
const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const defaultIcon = computed(() => props.device.icon || 'air');
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

const iconFontSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

/** The drawn wind's size inside the pin (P13), in the same steps. */
const pinIconSize = iconFontSize;

/**
 * The pin's ring (P13): the speed the unit reports while it runs, and all the
 * way up for a unit that runs without publishing a percentage — the rule the
 * light and fan pins share.
 */
const ringValue = computed(() => (running.value ? (speedPercent.value ?? 100) : 0));

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

// ── Options ──────────────────────────────────────────────────────────────
function toOption(row: { value: unknown; label: unknown }): VentOption {
  const value = typeof row.value === 'number' || typeof row.value === 'string' ? row.value : String(row.value);
  return { value, label: String(row.label ?? '') };
}

onMounted(async () => {
  // The class's enum choices come from the options endpoint (they are not
  // stored on the device row); the build's own four modes are the fallback.
  const modeRow = props.device.deviceValues?.find((value) => value.valueKey === 'mode');
  const filterRow = props.device.deviceValues?.find((value) => value.valueKey === 'filter_status');
  try {
    if (modeRow?.id) {
      const options = await deviceValueOptionsStore.fetchOptions(modeRow.id);
      if (options.enum_options.length) fetchedModeOptions.value = options.enum_options.map(toOption);
    }
    if (filterRow?.id) {
      const options = await deviceValueOptionsStore.fetchOptions(filterRow.id);
      if (options.enum_options.length) fetchedFilterOptions.value = options.enum_options.map(toOption);
    }
  } catch {
    // Fall back to the build's own options.
  }
});
</script>

<style scoped>
/* ── Active states ───────────────────────────────────────────────────────
   The ventilation family's accent is warm amber (fan is sky, sauna amber);
   every state colour is surface-safe in both card styles and in dark mode. */
.vent-pin--active {
  border-color: rgb(245, 158, 11) !important;
}

.vent-pin__badge {
  position: absolute;
  right: 0.05rem;
  bottom: 0.05rem;
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  z-index: 2;
}
.vent-pin__badge--due {
  background: rgb(245, 158, 11);
}
.vent-pin__badge--replace {
  background: rgb(239, 68, 68);
}

@keyframes vent-breathe {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}
.vent-breathe {
  animation: vent-breathe 2.6s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .vent-breathe {
    animation: none !important;
  }
}

/* ── Running state ───────────────────────────────────────────────────────
   One `color` per state drives the border, the circle's tint and glow, the
   name, the mode · speed line and the airflow values, so light and dark stay
   in sync — the app theme is the `.dark` class, not the OS preference. */
.vent-card--active {
  color: rgb(180, 83, 9);
  border-color: color-mix(in srgb, currentColor 55%, transparent) !important;
  box-shadow:
    0 0 10px color-mix(in srgb, currentColor 22%, transparent),
    0 0 26px color-mix(in srgb, currentColor 8%, transparent);
}
.dark .vent-card--active {
  color: rgb(251, 191, 36);
}
.vent-card--active .device-icon--circle {
  background: color-mix(in srgb, currentColor 14%, var(--icon-circle-fill));
  border-color: color-mix(in srgb, currentColor 45%, transparent);
  box-shadow:
    0 0 10px color-mix(in srgb, currentColor 26%, transparent),
    0 0 24px color-mix(in srgb, currentColor 10%, transparent);
}
/* The layered utilities paint the name, the value and the glyph, so each needs
   the tile's own colour spelled out. */
.vent-card--active .device-name,
.vent-card--active .device-current-value,
.vent-card--active .vent-train__value,
.vent-card--active .device-icon--circle .material-symbols-outlined {
  color: currentColor !important;
}

/* ── Compact card rows ───────────────────────────────────────────────────
   The card is the tiles' own head plus three rows: the four modes, the airflow
   train and the bypass/filter state. Every size is em against the button's
   container-query font scale, so the card keeps its proportions at any width
   instead of mixing fixed rem sizes into a scaled card. */
.vent-card-layout {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  margin-top: 0;
}

.device-button-head {
  display: flex;
  align-items: center;
  gap: 0.55em;
  min-width: 0;
}

.device-button-text {
  display: flex;
  flex-direction: column;
  gap: 0.1em;
  min-width: 0;
}

/* The name stays small; the mode under it carries the weight */
.vent-card-layout .device-name {
  font-size: 0.62em;
  min-width: 0;
}

.device-current-value {
  display: flex;
  align-items: baseline;
  gap: 0.3em;
  font-size: var(--tile-status-size); /* same as .device-name--xs */
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.vent-value-unit {
  font-size: 0.72em;
  font-weight: 600;
  opacity: 0.75;
}

.device-icon--circle {
  width: 2.35em;
  height: 2.35em;
  margin-bottom: 0;
  border-radius: 50%;
  background: var(--icon-circle-fill);
  border: 1px solid rgba(148, 163, 184, 0.35);
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

/* The unit's own photo fills the circle */
.device-icon--circle :deep(.device-thumb) {
  width: 100%;
  height: 100%;
}

.vent-chip-row {
  display: grid;
  gap: 0.25em;
  margin-top: 0.5em;
}

.vent-chip {
  min-width: 0;
  height: 1.7em;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5em;
  padding: 0 0.2em;
  font-weight: 700;
  text-align: center;
  color: rgb(107 114 128);
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.18);
  cursor: pointer;
  user-select: none;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease;
}

.vent-chip__label {
  font-size: 0.55em;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vent-chip--active {
  transform: translateY(-1px);
  background: rgba(245, 158, 11, 0.94);
  border-color: rgba(217, 119, 6, 0.95);
  color: rgb(24, 24, 27);
  box-shadow: 0 2px 10px rgba(245, 158, 11, 0.26);
}
.dark .vent-chip--active {
  background: rgb(217, 119, 6);
  border-color: rgb(180, 83, 9);
  color: rgb(255, 255, 255);
}

/* The Off chip reads as "not running" even when selected. */
.vent-chip--off.vent-chip--active {
  background: rgba(203, 213, 225, 0.96);
  border-color: rgba(148, 163, 184, 0.95);
  color: rgb(15, 23, 42);
  box-shadow: none;
}

/* ── Airflow readout ───────────────────────────────────────────────────── */
/* The Outdoor → Supply · Extract → Exhaust chain, on one line under the mode:
   the labels are dropped on narrow cards (the tooltip and the control view
   carry the full names), so only the four readings and the two hops remain. */
.vent-train {
  display: flex;
  align-items: baseline;
  gap: 0.3em;
  margin-top: 0.15em;
  font-size: 0.55em;
  line-height: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
}
.vent-train__value {
  font-weight: 700;
  color: rgb(107 114 128);
}
.dark .vent-train__value {
  color: rgb(156 163 175);
}
.vent-train__label {
  display: none;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgb(107 114 128);
}
.dark .vent-train__label {
  color: rgb(156 163 175);
}
.vent-train__arrow,
.vent-train__divider {
  color: rgb(148 163 184);
}

/* Wide tiles have room for the labels in front of each reading. */
@container device-button (min-width: 320px) {
  .vent-train__label {
    display: inline;
  }
}

/* ── Bypass + filter ───────────────────────────────────────────────────── */
.vent-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.3em;
  margin-top: 0.5em;
  min-width: 0;
}
.vent-status-chip {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.3em 0.7em;
  font-size: 0.52em;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgb(107 114 128);
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.18);
}
.vent-status-chip--bypass {
  cursor: pointer;
  user-select: none;
}
.vent-status-chip--bypass.vent-status-chip--active {
  background: rgba(245, 158, 11, 0.9);
  border-color: rgba(217, 119, 6, 0.95);
  color: rgb(24, 24, 27);
}
.dark .vent-status-chip--bypass.vent-status-chip--active {
  background: rgb(217, 119, 6);
  border-color: rgb(180, 83, 9);
  color: rgb(255, 255, 255);
}

.vent-filter-chip--ok,
.vent-status-chip.vent-filter-chip--ok {
  color: rgb(22, 101, 52);
  background: rgba(34, 197, 94, 0.13);
  border-color: rgba(34, 197, 94, 0.35);
}
.vent-filter-chip--due,
.vent-status-chip.vent-filter-chip--due {
  color: rgb(180, 83, 9);
  background: rgba(245, 158, 11, 0.16);
  border-color: rgba(245, 158, 11, 0.4);
}
.vent-filter-chip--replace,
.vent-status-chip.vent-filter-chip--replace {
  color: rgb(153, 27, 27);
  background: rgba(239, 68, 68, 0.14);
  border-color: rgba(239, 68, 68, 0.4);
}
.dark .vent-filter-chip--ok,
.dark .vent-status-chip.vent-filter-chip--ok {
  color: rgb(134, 239, 172);
}
.dark .vent-filter-chip--due,
.dark .vent-status-chip.vent-filter-chip--due {
  color: rgb(252, 211, 77);
}
.dark .vent-filter-chip--replace,
.dark .vent-status-chip.vent-filter-chip--replace {
  color: rgb(252, 165, 165);
}

/* ── Control sheet (expanded) ──────────────────────────────────────────── */
/* The body of the control sheet: the airflow train, the mode chips, the speed
   slider, the bypass row and the filter/humidity tiles. Every accent reads
   `--sheet-accent`, which the body root hands down (the vent family's amber). */
.vent-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(1rem * var(--ui-space-factor));
  min-width: 0;
}

/* The train — Outdoor → Supply · Extract → Exhaust in one bordered card, the
   sketch's own recipe. */
.vent-train-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.375rem;
  padding: calc(0.8125rem * var(--ui-space-factor)) calc(0.875rem * var(--ui-space-factor));
  border: 1px solid var(--glass-edge);
  border-radius: calc(0.875rem * var(--ui-radius-factor));
  background: rgba(148, 163, 184, 0.08);
}

.dark .vent-train-card {
  background: rgba(255, 255, 255, 0.03);
}

.vent-stop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1875rem;
  min-width: 0;
  color: var(--sheet-accent);
}

.vent-stop-value {
  font-size: 0.9063rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgb(var(--gray-900));
}

.dark .vent-stop-value {
  color: #fff;
}

.vent-stop-label {
  font-size: 0.625rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
  white-space: nowrap;
}

.dark .vent-stop-label {
  color: rgb(var(--gray-400));
}

.vent-arrow {
  align-self: center;
  padding-bottom: 0.75rem;
  font-size: 0.9375rem;
  color: rgb(var(--gray-400));
}

.vent-divider {
  width: 1px;
  align-self: stretch;
  background: var(--glass-edge);
  margin: 0 0.375rem;
}

/* The mode chips. */
.vent-chips {
  display: flex;
  flex-wrap: wrap;
  gap: calc(0.5rem * var(--ui-space-factor));
}

/* The speed slider with its own label row. */
.vent-speed {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}

.vent-row-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.vent-row-key {
  font-size: 0.8125rem;
  color: rgb(var(--gray-600));
}

.dark .vent-row-key {
  color: rgb(var(--gray-300));
}

.vent-row-value {
  font-size: 0.9375rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--gray-900));
}

.dark .vent-row-value {
  color: #fff;
}

/* The readings under the bypass row: the filter tile (its own tint rides on
   the tile) and the indoor humidity. */
.vent-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9.375rem, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

.vent-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35em;
  margin: 0;
  font-size: 0.6875rem;
  color: rgb(var(--gray-500));
}

.dark .vent-meta {
  color: rgb(var(--gray-400));
}
</style>
